import {t} from "@lingui/core/macro";

type CountryCode = string;
type CountryData = {
    country: string;
    minutes: number;
};

// sync-start:countries-list 1546297744 ./apps/khanacademy/src/auth/components/school-selector/countries.ts
// sync-start:countries-list 1217142767 https://github.com/Khan/webapp/blob/master/pkg/services_shared/intl/countries.go
// Our list of countries comes from the ISO 3166 standard for country codes:
// https://www.iso.org/obp/ui/#search
//
// We made a decision to take these names directly from ISO as to avoid political confusion
// https://khanacademy.atlassian.net/wiki/spaces/CP/pages/1498120417/Product+Decision+Record+Country+Lists

//  `minutes` represents the tier of total minutes learning in that country
//  0: < 100k, 1: 100k-999k, 2: 1M-100M, 3: 100M-1B, 4: 1B+
// This info is used by the langauge map to determine each country's color
const getCountryCodeToData = (): Map<CountryCode, CountryData> =>
    new Map([
        ["AD", {country: t`Andorra`, minutes: 3}],
        ["AE", {country: t`United Arab Emirates (the)`, minutes: 2}],
        ["AF", {country: t`Afghanistan`, minutes: 3}],
        ["AG", {country: t`Antigua and Barbuda`, minutes: 3}],
        ["AI", {country: t`Anguilla`, minutes: 4}],
        ["AL", {country: t`Albania`, minutes: 2}],
        ["AM", {country: t`Armenia`, minutes: 2}],
        ["AO", {country: t`Angola`, minutes: 3}],
        ["AQ", {country: t`Antarctica`, minutes: 4}],
        ["AR", {country: t`Argentina`, minutes: 2}],
        ["AS", {country: t`American Samoa`, minutes: 3}],
        ["AT", {country: t`Austria`, minutes: 2}],
        ["AU", {country: t`Australia`, minutes: 2}],
        ["AW", {country: t`Aruba`, minutes: 3}],
        ["AX", {country: t`Åland Islands`, minutes: 4}],
        ["AZ", {country: t`Azerbaijan`, minutes: 2}],
        ["BA", {country: t`Bosnia and Herzegovina`, minutes: 2}],
        ["BB", {country: t`Barbados`, minutes: 3}],
        ["BD", {country: t`Bangladesh`, minutes: 2}],
        ["BE", {country: t`Belgium`, minutes: 2}],
        ["BF", {country: t`Burkina Faso`, minutes: 3}],
        ["BG", {country: t`Bulgaria`, minutes: 2}],
        ["BH", {country: t`Bahrain`, minutes: 2}],
        ["BI", {country: t`Burundi`, minutes: 3}],
        ["BJ", {country: t`Benin`, minutes: 3}],
        ["BL", {country: t`Saint Barthélemy`, minutes: 4}],
        ["BM", {country: t`Bermuda`, minutes: 3}],
        ["BN", {country: t`Brunei Darussalam`, minutes: 3}],
        ["BO", {country: t`Bolivia (Plurinational State of)`, minutes: 2}],
        ["BQ", {country: t`Bonaire, Sint Eustatius and Saba`, minutes: 4}],
        ["BR", {country: t`Brazil`, minutes: 1}],
        ["BS", {country: t`Bahamas (the)`, minutes: 2}],
        ["BT", {country: t`Bhutan`, minutes: 3}],
        ["BV", {country: t`Bouvet Island`, minutes: 4}],
        ["BW", {country: t`Botswana`, minutes: 2}],
        ["BY", {country: t`Belarus`, minutes: 3}],
        ["BZ", {country: t`Belize`, minutes: 3}],
        ["CA", {country: t`Canada`, minutes: 1}],
        ["CC", {country: t`Cocos (Keeling) Islands (the)`, minutes: 4}],
        [
            "CD",
            {
                country: t`Congo (the Democratic Republic of the)`,
                minutes: 3,
            },
        ],
        ["CF", {country: t`Central African Republic (the)`, minutes: 4}],
        ["CG", {country: t`Congo (the)`, minutes: 4}],
        ["CH", {country: t`Switzerland`, minutes: 2}],
        ["CI", {country: t`Côte d'Ivoire`, minutes: 3}],
        ["CK", {country: t`Cook Islands (the)`, minutes: 4}],
        ["CL", {country: t`Chile`, minutes: 2}],
        ["CM", {country: t`Cameroon`, minutes: 3}],
        ["CN", {country: t`China`, minutes: 2}],
        ["CO", {country: t`Colombia`, minutes: 2}],
        ["CR", {country: t`Costa Rica`, minutes: 2}],
        ["CU", {country: t`Cuba`, minutes: 4}],
        ["CV", {country: t`Cabo Verde`, minutes: 3}],
        ["CW", {country: t`Curaçao`, minutes: 3}],
        ["CX", {country: t`Christmas Island`, minutes: 4}],
        ["CY", {country: t`Cyprus`, minutes: 2}],
        ["CZ", {country: t`Czechia`, minutes: 2}],
        ["DE", {country: t`Germany`, minutes: 2}],
        ["DJ", {country: t`Djibouti`, minutes: 4}],
        ["DK", {country: t`Denmark`, minutes: 2}],
        ["DM", {country: t`Dominica`, minutes: 4}],
        ["DO", {country: t`Dominican Republic (the)`, minutes: 2}],
        ["DZ", {country: t`Algeria`, minutes: 2}],
        ["EC", {country: t`Ecuador`, minutes: 2}],
        ["EE", {country: t`Estonia`, minutes: 2}],
        ["EG", {country: t`Egypt`, minutes: 2}],
        ["EH", {country: t`Western Sahara`, minutes: 4}],
        ["ER", {country: t`Eritrea`, minutes: 4}],
        ["ES", {country: t`Spain`, minutes: 2}],
        ["ET", {country: t`Ethiopia`, minutes: 2}],
        ["FI", {country: t`Finland`, minutes: 2}],
        ["FJ", {country: t`Fiji`, minutes: 3}],
        ["FK", {country: t`Falkland Islands (the) [Malvinas]`, minutes: 4}],
        ["FM", {country: t`Micronesia (Federated States of)`, minutes: 4}],
        ["FO", {country: t`Faroe Islands (the)`, minutes: 3}],
        ["FR", {country: t`France`, minutes: 2}],
        ["GA", {country: t`Gabon`, minutes: 3}],
        [
            "GB",
            {
                country: t`United Kingdom of Great Britain and Northern Ireland (the)`,
                minutes: 1,
            },
        ],
        ["GD", {country: t`Grenada`, minutes: 3}],
        ["GE", {country: t`Georgia`, minutes: 2}],
        ["GF", {country: t`French Guiana`, minutes: 4}],
        ["GG", {country: t`Guernsey`, minutes: 4}],
        ["GH", {country: t`Ghana`, minutes: 2}],
        ["GI", {country: t`Gibraltar`, minutes: 4}],
        ["GL", {country: t`Greenland`, minutes: 4}],
        ["GM", {country: t`Gambia (the)`, minutes: 3}],
        ["GN", {country: t`Guinea`, minutes: 4}],
        ["GP", {country: t`Guadeloupe`, minutes: 3}],
        ["GQ", {country: t`Equatorial Guinea`, minutes: 4}],
        ["GR", {country: t`Greece`, minutes: 2}],
        [
            "GS",
            {
                country: t`South Georgia and the South Sandwich Islands`,
                minutes: 5,
            },
        ],
        ["GT", {country: t`Guatemala`, minutes: 2}],
        ["GU", {country: t`Guam`, minutes: 2}],
        ["GW", {country: t`Guinea-Bissau`, minutes: 4}],
        ["GY", {country: t`Guyana`, minutes: 3}],
        ["HK", {country: t`Hong Kong`, minutes: 2}],
        ["HM", {country: t`Heard Island and McDonald Islands`, minutes: 4}],
        ["HN", {country: t`Honduras`, minutes: 2}],
        ["HR", {country: t`Croatia`, minutes: 2}],
        ["HT", {country: t`Haiti`, minutes: 3}],
        ["HU", {country: t`Hungary`, minutes: 2}],
        ["ID", {country: t`Indonesia`, minutes: 2}],
        ["IE", {country: t`Ireland`, minutes: 2}],
        ["IL", {country: t`Israel`, minutes: 2}],
        ["IM", {country: t`Isle of Man`, minutes: 3}],
        ["IN", {country: t`India`, minutes: 1}],
        [
            "IO",
            {
                country: t`British Indian Ocean Territory (the)`,
                minutes: 4,
            },
        ],
        ["IQ", {country: t`Iraq`, minutes: 2}],
        ["IR", {country: t`Iran (Islamic Republic of)`, minutes: 2}],
        ["IS", {country: t`Iceland`, minutes: 2}],
        ["IT", {country: t`Italy`, minutes: 2}],
        ["JE", {country: t`Jersey`, minutes: 4}],
        ["JM", {country: t`Jamaica`, minutes: 2}],
        ["JO", {country: t`Jordan`, minutes: 2}],
        ["JP", {country: t`Japan`, minutes: 2}],
        ["KE", {country: t`Kenya`, minutes: 2}],
        ["KG", {country: t`Kyrgyzstan`, minutes: 2}],
        ["KH", {country: t`Cambodia`, minutes: 2}],
        ["KI", {country: t`Kiribati`, minutes: 4}],
        ["KM", {country: t`Comoros (the)`, minutes: 4}],
        ["KN", {country: t`Saint Kitts and Nevis`, minutes: 3}],
        [
            "KP",
            {
                country: t`Korea (the Democratic People's Republic of)`,
                minutes: 4,
            },
        ],
        ["KR", {country: t`Korea (the Republic of)`, minutes: 2}],
        ["KW", {country: t`Kuwait`, minutes: 2}],
        ["KY", {country: t`Cayman Islands (the)`, minutes: 3}],
        ["KZ", {country: t`Kazakhstan`, minutes: 2}],
        [
            "LA",
            {
                country: t`Lao People's Democratic Republic (the)`,
                minutes: 3,
            },
        ],
        ["LB", {country: t`Lebanon`, minutes: 2}],
        ["LC", {country: t`Saint Lucia`, minutes: 3}],
        ["LI", {country: t`Liechtenstein`, minutes: 4}],
        ["LK", {country: t`Sri Lanka`, minutes: 2}],
        ["LR", {country: t`Liberia`, minutes: 3}],
        ["LS", {country: t`Lesotho`, minutes: 3}],
        ["LT", {country: t`Lithuania`, minutes: 2}],
        ["LU", {country: t`Luxembourg`, minutes: 3}],
        ["LV", {country: t`Latvia`, minutes: 2}],
        ["LY", {country: t`Libya`, minutes: 3}],
        ["MA", {country: t`Morocco`, minutes: 2}],
        ["MC", {country: t`Monaco`, minutes: 4}],
        ["MD", {country: t`Moldova (the Republic of)`, minutes: 3}],
        ["ME", {country: t`Montenegro`, minutes: 3}],
        ["MF", {country: t`Saint Martin (French part)`, minutes: 4}],
        ["MG", {country: t`Madagascar`, minutes: 3}],
        ["MH", {country: t`Marshall Islands (the)`, minutes: 4}],
        ["MK", {country: t`North Macedonia`, minutes: 2}],
        ["ML", {country: t`Mali`, minutes: 3}],
        ["MM", {country: t`Myanmar`, minutes: 2}],
        ["MN", {country: t`Mongolia`, minutes: 2}],
        ["MO", {country: t`Macao`, minutes: 3}],
        ["MP", {country: t`Northern Mariana Islands (the)`, minutes: 3}],
        ["MQ", {country: t`Martinique`, minutes: 3}],
        ["MR", {country: t`Mauritania`, minutes: 3}],
        ["MS", {country: t`Montserrat`, minutes: 4}],
        ["MT", {country: t`Malta`, minutes: 3}],
        ["MU", {country: t`Mauritius`, minutes: 3}],
        ["MV", {country: t`Maldives`, minutes: 3}],
        ["MW", {country: t`Malawi`, minutes: 3}],
        ["MX", {country: t`Mexico`, minutes: 1}],
        ["MY", {country: t`Malaysia`, minutes: 2}],
        ["MZ", {country: t`Mozambique`, minutes: 3}],
        ["NA", {country: t`Namibia`, minutes: 3}],
        ["NC", {country: t`New Caledonia`, minutes: 3}],
        ["NE", {country: t`Niger (the)`, minutes: 4}],
        ["NF", {country: t`Norfolk Island`, minutes: 4}],
        ["NG", {country: t`Nigeria`, minutes: 2}],
        ["NI", {country: t`Nicaragua`, minutes: 2}],
        ["NL", {country: t`Netherlands (the)`, minutes: 2}],
        ["NO", {country: t`Norway`, minutes: 2}],
        ["NP", {country: t`Nepal`, minutes: 2}],
        ["NR", {country: t`Nauru`, minutes: 4}],
        ["NU", {country: t`Niue`, minutes: 4}],
        ["NZ", {country: t`New Zealand`, minutes: 2}],
        ["OM", {country: t`Oman`, minutes: 2}],
        ["PA", {country: t`Panama`, minutes: 2}],
        ["PE", {country: t`Peru`, minutes: 2}],
        ["PF", {country: t`French Polynesia`, minutes: 3}],
        ["PG", {country: t`Papua New Guinea`, minutes: 3}],
        ["PH", {country: t`Philippines (the)`, minutes: 2}],
        ["PK", {country: t`Pakistan`, minutes: 2}],
        ["PL", {country: t`Poland`, minutes: 2}],
        ["PM", {country: t`Saint Pierre and Miquelon`, minutes: 4}],
        ["PN", {country: t`Pitcairn`, minutes: 4}],
        ["PR", {country: t`Puerto Rico`, minutes: 2}],
        ["PS", {country: t`Palestine, State of`, minutes: 3}],
        ["PT", {country: t`Portugal`, minutes: 2}],
        ["PW", {country: t`Palau`, minutes: 4}],
        ["PY", {country: t`Paraguay`, minutes: 2}],
        ["QA", {country: t`Qatar`, minutes: 2}],
        ["RE", {country: t`Réunion`, minutes: 3}],
        ["RO", {country: t`Romania`, minutes: 2}],
        ["RS", {country: t`Serbia`, minutes: 2}],
        ["RU", {country: t`Russian Federation (the)`, minutes: 2}],
        ["RW", {country: t`Rwanda`, minutes: 2}],
        ["SA", {country: t`Saudi Arabia`, minutes: 2}],
        ["SB", {country: t`Solomon Islands`, minutes: 4}],
        ["SC", {country: t`Seychelles`, minutes: 4}],
        ["SD", {country: t`Sudan (the)`, minutes: 3}],
        ["SE", {country: t`Sweden`, minutes: 2}],
        ["SG", {country: t`Singapore`, minutes: 2}],
        [
            "SH",
            {
                country: t`Saint Helena, Ascension and Tristan da Cunha`,
                minutes: 4,
            },
        ],
        ["SI", {country: t`Slovenia`, minutes: 2}],
        ["SJ", {country: t`Svalbard and Jan Mayen`, minutes: 4}],
        ["SK", {country: t`Slovakia`, minutes: 2}],
        ["SL", {country: t`Sierra Leone`, minutes: 3}],
        ["SM", {country: t`San Marino`, minutes: 4}],
        ["SN", {country: t`Senegal`, minutes: 2}],
        ["SO", {country: t`Somalia`, minutes: 3}],
        ["SR", {country: t`Suriname`, minutes: 3}],
        ["SS", {country: t`South Sudan`, minutes: 4}],
        ["ST", {country: t`Sao Tome and Principe`, minutes: 4}],
        ["SV", {country: t`El Salvador`, minutes: 2}],
        ["SX", {country: t`Sint Maarten (Dutch part)`, minutes: 3}],
        ["SY", {country: t`Syrian Arab Republic (the)`, minutes: 3}],
        ["SZ", {country: t`Eswatini`, minutes: 3}],
        ["TC", {country: t`Turks and Caicos Islands (the)`, minutes: 3}],
        ["TD", {country: t`Chad`, minutes: 4}],
        ["TF", {country: t`French Southern Territories (the)`, minutes: 4}],
        ["TG", {country: t`Togo`, minutes: 4}],
        ["TH", {country: t`Thailand`, minutes: 2}],
        ["TJ", {country: t`Tajikistan`, minutes: 3}],
        ["TK", {country: t`Tokelau`, minutes: 4}],
        ["TL", {country: t`Timor-Leste`, minutes: 3}],
        ["TM", {country: t`Turkmenistan`, minutes: 3}],
        ["TN", {country: t`Tunisia`, minutes: 2}],
        ["TO", {country: t`Tonga`, minutes: 4}],
        ["TR", {country: t`Turkey`, minutes: 2}],
        ["TT", {country: t`Trinidad and Tobago`, minutes: 2}],
        ["TV", {country: t`Tuvalu`, minutes: 4}],
        ["TW", {country: t`Taiwan (Province of China)`, minutes: 2}],
        ["TZ", {country: t`Tanzania, the United Republic of`, minutes: 2}],
        ["UA", {country: t`Ukraine`, minutes: 2}],
        ["UG", {country: t`Uganda`, minutes: 2}],
        [
            "UM",
            {
                country: t`United States Minor Outlying Islands (the)`,
                minutes: 5,
            },
        ],
        ["US", {country: t`United States of America (the)`, minutes: 0}],
        ["UY", {country: t`Uruguay`, minutes: 2}],
        ["UZ", {country: t`Uzbekistan`, minutes: 2}],
        ["VA", {country: t`Holy See (the)`, minutes: 4}],
        ["VC", {country: t`Saint Vincent and the Grenadines`, minutes: 3}],
        ["VE", {country: t`Venezuela (Bolivarian Republic of)`, minutes: 2}],
        ["VG", {country: t`Virgin Islands (British)`, minutes: 4}],
        ["VI", {country: t`Virgin Islands (U.S.)`, minutes: 3}],
        ["VN", {country: t`Viet Nam`, minutes: 2}],
        ["VU", {country: t`Vanuatu`, minutes: 4}],
        ["WF", {country: t`Wallis and Futuna`, minutes: 4}],
        ["WS", {country: t`Samoa`, minutes: 4}],
        ["XK", {country: t`Kosovo`, minutes: 3}],
        ["YE", {country: t`Yemen`, minutes: 3}],
        ["YT", {country: t`Mayotte`, minutes: 4}],
        ["ZA", {country: t`South Africa`, minutes: 2}],
        ["ZM", {country: t`Zambia`, minutes: 2}],
        ["ZW", {country: t`Zimbabwe`, minutes: 2}],
    ]);
// sync-end:countries-list

export const safeGetCountryByCode = (isoCode: CountryCode): CountryData => {
    const country = getCountryCodeToData().get(isoCode);
    return country ? country : {country: "", minutes: 0};
};

export default getCountryCodeToData;
